import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Heart, Minus, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { toast } from "sonner";
import { trackViewItem } from "../lib/analytics";
import { formatPrice } from "../lib/format";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const { addToCart, toggleWishlist, inWishlist } = useShop();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setSize(data.sizes?.[0] || "");
      trackViewItem(data);
      // Server-side event log for admin activity feed
      api.post("/events", { name: "product_view", props: { product_id: data.product_id, name: data.name, price: data.price } }).catch(() => {});
    });
  }, [id]);

  if (!product) return <div className="min-h-screen flex items-center justify-center text-muted text-xs uppercase tracking-[0.3em]">Loading…</div>;

  const liked = inWishlist(product.product_id);
  const soldOut = (product.stock ?? 0) <= 0;
  const onlyOneLeft = !soldOut && (product.stock ?? 0) === 1;

  const handleAdd = () => {
    if (soldOut) { toast.error("This piece is currently sold out"); return; }
    if (!size) { toast.error("Please select a size"); return; }
    addToCart(product, size, qty);
    toast.success("Added to your bag");
    api.post("/events", { name: "add_to_cart", props: { product_id: product.product_id, name: product.name, price: product.price, size, qty } }).catch(() => {});
  };

  const total = product.images.length;
  const goPrev = () => setActive((i) => (i - 1 + total) % total);
  const goNext = () => setActive((i) => (i + 1) % total);

  return (
    <div className="pt-28 pb-24">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 px-6 md:px-12">
        {/* Image gallery */}
        <div>
          <div className="relative aspect-[3/4] overflow-hidden bg-[#141414] group">
            <img src={product.images[active]} alt={product.name} className="w-full h-full object-cover" data-testid="product-hero-image" />
            {total > 1 && (
              <>
                <button
                  onClick={goPrev}
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm text-gold opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity flex items-center justify-center"
                  data-testid="image-prev-btn"
                ><ChevronLeft size={18} strokeWidth={1.5} /></button>
                <button
                  onClick={goNext}
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm text-gold opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity flex items-center justify-center"
                  data-testid="image-next-btn"
                ><ChevronRight size={18} strokeWidth={1.5} /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === active ? "bg-gold" : "bg-white/30 hover:bg-white/60"}`}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {product.images.map((src, i) => (
              <button
                key={src}
                onClick={() => setActive(i)}
                className={`aspect-[3/4] overflow-hidden bg-[#141414] ${i === active ? "ring-1 ring-[#C8A97E]" : "opacity-60 hover:opacity-100"}`}
                data-testid={`thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          {product.videos?.length > 0 && (
            <div className="mt-6 space-y-3">
              {product.videos.map((src) => (
                <video
                  key={src}
                  src={src}
                  controls
                  playsInline
                  className="w-full bg-[#141414]"
                  data-testid="product-video"
                />
              ))}
            </div>
          )}
          <p className="text-[11px] text-text italic mt-4 tracking-normal normal-case leading-relaxed" data-testid="product-disclaimer">
            The actual product may vary from the images
          </p>
        </div>

        {/* Details */}
        <div className="lg:sticky lg:top-32 lg:self-start space-y-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{product.category}</p>
          <h1 className="font-serif text-4xl md:text-5xl">{product.name}</h1>
          <p className="text-2xl text-gold">{formatPrice(product.price)}</p>
          {soldOut && (
            <p className="text-xs uppercase tracking-[0.3em] text-red-400" data-testid="sold-out-label">Sold Out</p>
          )}
          {onlyOneLeft && (
            <p className="text-xs uppercase tracking-[0.3em] text-gold" data-testid="low-stock-label">Only 1 left</p>
          )}
          <div className="divider-gold" />
          <p className="text-muted leading-relaxed max-w-md">{product.description}</p>

          <div className="space-y-3 pt-4">
            <span className="text-xs uppercase tracking-[0.2em] text-muted">Size</span>
            <div className="flex flex-wrap gap-3">
              {product.sizes.map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`w-14 h-12 text-xs uppercase tracking-[0.2em] transition-colors ${size === s ? "border border-[#C8A97E] text-gold" : "border border-[rgba(244,244,240,0.1)] text-muted hover:text-gold hover:border-[#C8A97E]"}`}
                  data-testid={`size-${s}`}
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center border border-[rgba(244,244,240,0.1)]">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-3 text-muted hover:text-gold"><Minus size={14} /></button>
              <span className="px-5 text-sm">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="p-3 text-muted hover:text-gold"><Plus size={14} /></button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              className="btn-gold flex-1"
              onClick={handleAdd}
              disabled={soldOut}
              data-testid="add-to-cart-btn"
            >
              {soldOut ? "Sold Out" : "Add to Bag"}
            </button>
            <button
              onClick={() => toggleWishlist(product)}
              className="btn-outline px-5"
              data-testid="wishlist-btn"
            >
              <Heart size={16} strokeWidth={1.5} fill={liked ? "#C8A97E" : "none"} />
            </button>
          </div>

          <div className="pt-6 border-t border-[rgba(244,244,240,0.06)] space-y-2 text-xs uppercase tracking-[0.2em] text-muted">
            <p>Complimentary shipping on orders above ₹1,000</p>
            <p>Crafted in limited runs</p>
          </div>
        </div>
      </div>
    </div>
  );
}
